import { useEffect, useState } from "react";
import {
  AlertCircle,
  CheckCircle,
  Database,
  FileText,
  Download,
  Loader2,
  Paperclip,
  Search,
  Upload,
  X,
} from "lucide-react";
import {
  analyzeDocumentHubBatch,
  getDocumentStatus,
  listDocumentHub,
  setComplianceContext,
  uploadDocument,
  uploadDocumentBatch,
} from "../../services/compliance-api";
import { useComplianceAuth } from "../../context/ComplianceAuthContext";
import type {
  ComplianceDocumentType,
  BatchDocumentUploadResponse,
  BatchDocumentUploadItem,
  DocumentHubItem,
  DocumentUploadResponse,
  DocumentStatusResponse,
  ShipmentProfile,
} from "../../types/compliance";

const DOCUMENT_TYPES: { value: ComplianceDocumentType; label: string }[] = [
  { value: "invoice", label: "Invoice" },
  { value: "ecmr", label: "eCMR" },
  { value: "packing_list", label: "Packing List" },
  { value: "delivery_note", label: "Delivery Note" },
  { value: "customs_declaration", label: "Customs Declaration" },
  { value: "bill_of_lading", label: "Bill of Lading" },
  { value: "air_waybill", label: "Air Waybill" },
  { value: "certificate_of_origin", label: "Certificate of Origin" },
  { value: "eur1", label: "EUR.1 Certificate" },
  { value: "supplier_declaration", label: "Supplier Declaration" },
  { value: "export_license", label: "Export License" },
  { value: "import_license", label: "Import License" },
  { value: "customs_release", label: "Customs Release" },
  { value: "proof_of_origin", label: "Proof of Origin" },
  { value: "vat_evidence", label: "VAT Evidence" },
  { value: "proof_of_delivery", label: "Proof of Delivery" },
  { value: "safety_data_sheet", label: "Safety Data Sheet" },
  {
    value: "dangerous_goods_declaration",
    label: "Dangerous Goods Declaration",
  },
  { value: "temperature_log", label: "Temperature Log" },
  { value: "import_export_license", label: "Import/Export License" },
  {
    value: "product_compliance_certificate",
    label: "Product Compliance Certificate",
  },
  {
    value: "subcontractor_carrier_record",
    label: "Subcontractor Carrier Record",
  },
  {
    value: "carrier_insurance_certificate",
    label: "Carrier Insurance Certificate",
  },
  { value: "inspection_report", label: "Inspection Report" },
];

type BatchSlot = {
  documentType: ComplianceDocumentType;
  label: string;
  filename: string;
  fileBase64: string | null;
  mimeType: string;
  sourceText: string;
};

const BATCH_DOCUMENT_SLOTS: BatchSlot[] = DOCUMENT_TYPES.map((doc) => ({
  documentType: doc.value,
  label: doc.label,
  filename: "",
  fileBase64: null,
  mimeType: "text/plain",
  sourceText: "",
}));

const SHIPMENT_PROFILE_OPTIONS: Array<{
  key: keyof ShipmentProfile;
  label: string;
}> = [
  { key: "dangerous_goods", label: "Dangerous goods" },
  { key: "cold_chain", label: "Cold chain" },
  { key: "regulated_goods", label: "Regulated goods" },
  { key: "preferential_origin", label: "Preferential origin claim" },
  { key: "subcontracted_transport", label: "Subcontracted carrier" },
  { key: "high_value", label: "High-value shipment" },
  { key: "delivery_completed", label: "Delivery completed" },
];

function displayFieldName(value: string): string {
  return value.replace(/_/g, " ");
}

function displayDocumentType(value: string): string {
  return value.replace(/_/g, " ");
}

function sanitizePdfText(value: string): string {
  return value
    .replace(/[–—]/g, "-")
    .replace(/…/g, "...")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[^\x09\x0A\x0D\x20-\x7E]/g, " ");
}

function escapePdfText(value: string): string {
  return sanitizePdfText(value)
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
}

function wrapText(value: string, maxLength = 96): string[] {
  const words = sanitizePdfText(value).split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (next.length > maxLength && line) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  return lines.length ? lines : [""];
}

function wrapPdfCell(value: string, width: number, fontSize: number): string[] {
  const maxChars = Math.max(8, Math.floor(width / (fontSize * 0.52)));
  return wrapText(value || "Missing", maxChars).slice(0, 4);
}

function createBatchReportPdfBlob(result: BatchDocumentUploadResponse): Blob {
  const pageWidth = 842;
  const pageHeight = 595;
  const margin = 30;
  const topY = 552;
  const bottomY = 34;
  const contentWidth = pageWidth - margin * 2;
  const rowLineHeight = 9;
  const pages: string[][] = [[]];
  let y = topY;

  const currentPage = () => pages[pages.length - 1];
  const addPage = () => {
    pages.push([]);
    y = topY;
  };
  const ensureSpace = (height: number) => {
    if (y - height < bottomY) addPage();
  };
  const drawText = (
    text: string,
    x: number,
    textY: number,
    size = 9,
    font = "F1",
  ) => {
    currentPage().push(
      `BT /${font} ${size} Tf ${x} ${textY} Td (${escapePdfText(text)}) Tj ET`,
    );
  };
  const drawLine = (x1: number, y1: number, x2: number, y2: number) => {
    currentPage().push(`${x1} ${y1} m ${x2} ${y2} l S`);
  };
  const drawFilledRect = (
    x: number,
    rectY: number,
    width: number,
    height: number,
    gray: number,
  ) => {
    currentPage().push(`q ${gray} g ${x} ${rectY} ${width} ${height} re f Q`);
  };
  const getTableRowParts = (
    cells: string[],
    widths: number[],
    fontSize: number,
  ) => {
    const wrappedCells = cells.map((cell, index) =>
      wrapPdfCell(cell, widths[index] - 8, fontSize),
    );
    const rowHeight = Math.max(
      20,
      Math.max(...wrappedCells.map((cell) => cell.length)) * rowLineHeight + 10,
    );
    return { wrappedCells, rowHeight };
  };
  const addHeading = (text: string) => {
    ensureSpace(24);
    drawText(text, margin, y, 14, "F2");
    y -= 20;
  };
  const addParagraph = (text: string, indent = 0) => {
    wrapText(text, indent ? 92 : 100).forEach((line) => {
      ensureSpace(14);
      drawText(line, margin + indent, y, 9);
      y -= 12;
    });
  };
  const addGap = (height = 8) => {
    ensureSpace(height);
    y -= height;
  };
  const drawTableRow = (
    cells: string[],
    widths: number[],
    options: { header?: boolean; fontSize?: number } = {},
  ) => {
    const fontSize = options.fontSize ?? (options.header ? 7.5 : 7);
    const { wrappedCells, rowHeight } = getTableRowParts(
      cells,
      widths,
      fontSize,
    );
    ensureSpace(rowHeight);
    const rowTop = y;
    const rowBottom = y - rowHeight;
    let x = margin;
    if (options.header)
      drawFilledRect(margin, rowBottom, contentWidth, rowHeight, 0.92);
    cells.forEach((_, index) => {
      drawLine(x, rowTop, x, rowBottom);
      wrappedCells[index].forEach((line, lineIndex) => {
        drawText(
          line,
          x + 4,
          rowTop - 12 - lineIndex * rowLineHeight,
          fontSize,
          options.header ? "F2" : "F1",
        );
      });
      x += widths[index];
    });
    drawLine(margin + contentWidth, rowTop, margin + contentWidth, rowBottom);
    drawLine(margin, rowTop, margin + contentWidth, rowTop);
    drawLine(margin, rowBottom, margin + contentWidth, rowBottom);
    y = rowBottom;
  };

  drawText(
    "LightLink AI - Shipment Batch Analysis Report",
    margin,
    y,
    16,
    "F2",
  );
  y -= 18;
  drawText(`Generated: ${new Date().toLocaleString()}`, margin, y, 9);
  y -= 14;
  drawText(
    `Tenant: ${result.tenant_id}   Batch: ${result.batch_id}   Status: ${result.status}`,
    margin,
    y,
    9,
  );
  y -= 14;
  drawText(
    `Shipment: ${result.shipment_reference || "Not detected"}   Documents: ${result.documents.length}`,
    margin,
    y,
    9,
  );
  y -= 20;

  addHeading("Executive Summary");
  result.report_summary.forEach((item) => addParagraph(`- ${item}`, 10));
  addGap();

  addHeading("Missing & Mismatch Review");
  addParagraph(
    result.missing_document_types.length > 0
      ? `Missing document types: ${result.missing_document_types.map(displayDocumentType).join(", ")}`
      : "All required document types are present.",
  );
  if (result.mismatches.length > 0) {
    result.mismatches.forEach((item) => {
      addParagraph(`- ${item.message} Severity: ${item.severity}`, 10);
      Object.entries(item.values).forEach(([docType, value]) => {
        addParagraph(`${displayDocumentType(docType)}: ${value}`, 20);
      });
    });
  } else {
    addParagraph("No cross-document mismatches detected.");
  }
  addGap();

  if (result.document_requirements.length > 0) {
    addHeading("Logistics Audit Document Checklist");
    drawTableRow(
      ["Document", "Level", "Status", "Reason"],
      [165, 80, 80, contentWidth - 325],
      { header: true, fontSize: 7 },
    );
    result.document_requirements.forEach((item) => {
      drawTableRow(
        [item.label, item.level, item.status, item.reason],
        [165, 80, 80, contentWidth - 325],
      );
    });
    addGap(10);
  }

  addHeading("Compliance Field Matrix");
  const matrixWidths = [120, 136, 116, 126, 126, 156];
  const matrixHeader = ["Field", ...DOCUMENT_TYPES.map((doc) => doc.label)];
  const drawMatrixHeader = () =>
    drawTableRow(matrixHeader, matrixWidths, {
      header: true,
      fontSize: 7.5,
    });
  const drawMatrixRow = (cells: string[]) => {
    const rowHeight = getTableRowParts(cells, matrixWidths, 7).rowHeight;
    if (y - rowHeight < bottomY) {
      addPage();
      drawMatrixHeader();
    }
    drawTableRow(cells, matrixWidths);
  };
  drawMatrixHeader();
  const matrixEntries = Object.entries(result.field_matrix);
  if (matrixEntries.length === 0) {
    drawMatrixRow([
      "No structured fields were extracted from this batch.",
      "",
      "",
      "",
      "",
      "",
    ]);
  } else {
    matrixEntries.forEach(([field, values]) => {
      drawMatrixRow([
        displayFieldName(field),
        ...DOCUMENT_TYPES.map((doc) => values[doc.value] || "Missing"),
      ]);
    });
  }
  addGap(18);

  addHeading("Relational Document Tree");
  result.relationship_tree.forEach((node) => {
    addParagraph(
      `${displayDocumentType(node.document_type)} (${node.document_id})`,
    );
    addParagraph(`Linked docs: ${node.links.length || 0}`, 14);
    if (node.missing_fields.length > 0) {
      addParagraph(
        `Missing: ${node.missing_fields.map(displayFieldName).join(", ")}`,
        14,
      );
    }
  });
  addGap();

  addHeading("Document-Level Missing Information");
  result.documents.forEach((doc) => {
    addParagraph(
      `${displayDocumentType(doc.document_type)} (${doc.document_id})`,
    );
    addParagraph(
      doc.missing_required_fields.length > 0
        ? `Missing: ${doc.missing_required_fields.map(displayFieldName).join(", ")}`
        : "Required fields complete.",
      14,
    );
  });

  const objects: string[] = [];
  objects.push("<< /Type /Catalog /Pages 2 0 R >>");
  const pageObjectIds = pages.map((_, index) => 3 + index * 2);
  objects.push(
    `<< /Type /Pages /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pages.length} >>`,
  );

  pages.forEach((commands, index) => {
    const pageId = 3 + index * 2;
    const contentId = pageId + 1;
    const content = commands.join("\n");
    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> /F2 << /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >> >> >> /Contents ${contentId} 0 R >>`,
    );
    objects.push(
      `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
    );
  });

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${offset.toString().padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return new Blob([pdf], { type: "application/pdf" });
}

type ApiErrorShape = {
  code?: string;
  message?: string;
  response?: {
    status?: number;
    data?: {
      detail?: unknown;
    };
  };
};

function formatApiError(error: unknown, fallback: string): string {
  const apiError = error as ApiErrorShape;
  if (
    apiError?.code === "ECONNABORTED" ||
    apiError?.message?.toLowerCase().includes("timeout")
  ) {
    return "The request timed out while processing this large batch. Please wait and retry, or split into smaller batches.";
  }

  const status = apiError?.response?.status;
  const detail = apiError?.response?.data?.detail;

  if (status === 422) {
    if (Array.isArray(detail)) {
      const messages = detail
        .slice(0, 4)
        .map((item) => {
          const entry = item as { loc?: Array<string | number>; msg?: string };
          const field = Array.isArray(entry.loc)
            ? entry.loc.join(".")
            : "request";
          return `${field}: ${entry.msg ?? "validation error"}`;
        })
        .join(" | ");
      return `Validation failed (422): ${messages}`;
    }
    if (typeof detail === "string" && detail.trim()) {
      return `Validation failed (422): ${detail}`;
    }
    return "Validation failed (422): request payload did not match backend schema.";
  }

  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

export default function DocumentWorkspace() {
  const { credentials } = useComplianceAuth();
  const [tenantId, setTenantId] = useState(
    () => credentials?.tenantId ?? "default",
  );
  const [documentType, setDocumentType] =
    useState<ComplianceDocumentType>("invoice");
  const [sourceText, setSourceText] = useState("");
  const [filename, setFilename] = useState("");
  const [amendmentOfDocumentId, setAmendmentOfDocumentId] = useState("");
  const [amendmentReason, setAmendmentReason] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileBase64, setFileBase64] = useState<string | null>(null);
  const [fileMimeType, setFileMimeType] = useState<string>("text/plain");
  const [singleUploadDragActive, setSingleUploadDragActive] = useState(false);
  const [batchSlotDragActive, setBatchSlotDragActive] = useState<
    Record<string, boolean>
  >({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DocumentUploadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [batchSlots, setBatchSlots] = useState<BatchSlot[]>(() =>
    BATCH_DOCUMENT_SLOTS.map((slot) => ({ ...slot })),
  );
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchResult, setBatchResult] =
    useState<BatchDocumentUploadResponse | null>(null);
  const [batchAnalysisId, setBatchAnalysisId] = useState("");
  const [pendingBatchDocuments, setPendingBatchDocuments] = useState<
    BatchDocumentUploadItem[] | null
  >(null);
  const [showHubPrompt, setShowHubPrompt] = useState(false);
  const [hubDocuments, setHubDocuments] = useState<DocumentHubItem[]>([]);
  const [hubLoading, setHubLoading] = useState(false);
  const [hubHistoryLoading, setHubHistoryLoading] = useState(false);
  const [hubHistory, setHubHistory] = useState<DocumentHubItem[]>([]);
  const [shipmentProfile, setShipmentProfile] = useState<ShipmentProfile>({
    movement_type: "eu_export",
  });

  // Document status tracking
  const [trackDocId, setTrackDocId] = useState("");
  const [trackResult, setTrackResult] = useState<DocumentStatusResponse | null>(
    null,
  );
  const [trackLoading, setTrackLoading] = useState(false);

  setComplianceContext(tenantId, credentials?.role ?? "COMPLIANCE_ADMIN");

  useEffect(() => {
    const preventDefault = (event: DragEvent) => {
      if (event.dataTransfer?.types?.includes("Files")) {
        event.preventDefault();
      }
    };
    window.addEventListener("dragover", preventDefault);
    window.addEventListener("drop", preventDefault);
    return () => {
      window.removeEventListener("dragover", preventDefault);
      window.removeEventListener("drop", preventDefault);
    };
  }, []);

  useEffect(() => {
    refreshDocumentHubHistory();
  }, [tenantId, credentials?.role]);

  function attachSingleFile(file: File | undefined) {
    if (!file) return;
    setSelectedFile(file);
    setFilename(file.name);
    setFileMimeType(file.type || "application/octet-stream");
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setFileBase64(result.split(",")[1]);
    };
    reader.readAsDataURL(file);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    attachSingleFile(e.target.files?.[0]);
  }

  function handleSingleUploadDrop(e: React.DragEvent<HTMLElement>) {
    e.preventDefault();
    setSingleUploadDragActive(false);
    attachSingleFile(e.dataTransfer.files?.[0]);
  }

  function handleSingleUploadDragLeave(e: React.DragEvent<HTMLElement>) {
    const nextTarget = e.relatedTarget as Node | null;
    if (!nextTarget || !e.currentTarget.contains(nextTarget)) {
      setSingleUploadDragActive(false);
    }
  }

  function setBatchSlotDrag(
    documentType: ComplianceDocumentType,
    active: boolean,
  ) {
    setBatchSlotDragActive((current) => ({
      ...current,
      [documentType]: active,
    }));
  }

  function updateBatchSlot(
    documentType: ComplianceDocumentType,
    updates: Partial<BatchSlot>,
  ) {
    setBatchSlots((current) =>
      current.map((slot) =>
        slot.documentType === documentType ? { ...slot, ...updates } : slot,
      ),
    );
  }

  function handleBatchFileChange(
    documentType: ComplianceDocumentType,
    file: File | undefined,
  ) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const value = reader.result as string;
      updateBatchSlot(documentType, {
        filename: file.name,
        fileBase64: value.split(",")[1],
        mimeType: file.type || "application/octet-stream",
        sourceText: "",
      });
    };
    reader.readAsDataURL(file);
  }

  function handleBatchFileDrop(
    documentType: ComplianceDocumentType,
    e: React.DragEvent<HTMLElement>,
  ) {
    e.preventDefault();
    setBatchSlotDrag(documentType, false);
    handleBatchFileChange(documentType, e.dataTransfer.files?.[0]);
  }

  function handleBatchSlotDragLeave(
    documentType: ComplianceDocumentType,
    e: React.DragEvent<HTMLElement>,
  ) {
    const nextTarget = e.relatedTarget as Node | null;
    if (!nextTarget || !e.currentTarget.contains(nextTarget)) {
      setBatchSlotDrag(documentType, false);
    }
  }

  function clearBatchSlot(documentType: ComplianceDocumentType) {
    updateBatchSlot(documentType, {
      filename: "",
      fileBase64: null,
      mimeType: "text/plain",
      sourceText: "",
    });
  }

  function updateShipmentProfile(updates: Partial<ShipmentProfile>) {
    setShipmentProfile((current) => ({ ...current, ...updates }));
  }

  function clearFile() {
    setSelectedFile(null);
    setFileBase64(null);
    setFileMimeType("text/plain");
  }

  function downloadBatchReport() {
    if (!batchResult) return;
    const report = {
      generated_at: new Date().toISOString(),
      tenant_id: batchResult.tenant_id,
      batch_id: batchResult.batch_id,
      batch_name: batchResult.batch_name,
      shipment_reference: batchResult.shipment_reference,
      status: batchResult.status,
      summary: batchResult.report_summary,
      missing_document_types: batchResult.missing_document_types,
      document_requirements: batchResult.document_requirements,
      mismatches: batchResult.mismatches,
      relationship_tree: batchResult.relationship_tree,
      field_matrix: batchResult.field_matrix,
      documents: batchResult.documents,
    };
    const blob = new Blob([JSON.stringify(report, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${batchResult.batch_id}-compliance-report.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  function downloadBatchReportPdf() {
    if (!batchResult) return;
    const blob = createBatchReportPdfBlob(batchResult);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${batchResult.batch_id}-compliance-report.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function handleUpload() {
    const hasFile = !!fileBase64;
    const hasText = !!sourceText.trim();
    if (!hasFile && !hasText) {
      setError("Upload a file or paste source text.");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const resp = await uploadDocument({
        tenant_id: tenantId,
        document_type: documentType,
        ...(hasFile
          ? { document_content_base64: fileBase64!, mime_type: fileMimeType }
          : { source_text: sourceText, mime_type: "text/plain" }),
        filename: filename || undefined,
        amendment_of_document_id: amendmentOfDocumentId.trim() || undefined,
        amendment_reason: amendmentReason.trim() || undefined,
      });
      setResult(resp);
    } catch (err) {
      setError(
        formatApiError(
          err,
          "Upload failed. Is the compliance backend reachable through the LightLink proxy?",
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  function collectBatchDocuments(): BatchDocumentUploadItem[] {
    return batchSlots
      .filter((slot) => slot.fileBase64 || slot.sourceText.trim())
      .map((slot) => ({
        document_type: slot.documentType,
        filename: slot.filename || undefined,
        mime_type: slot.fileBase64 ? slot.mimeType : "text/plain",
        ...(slot.fileBase64
          ? { document_content_base64: slot.fileBase64 }
          : { source_text: slot.sourceText.trim() }),
      }));
  }

  async function refreshDocumentHub(batchId?: string) {
    setHubLoading(true);
    try {
      const hub = await listDocumentHub(batchId);
      setHubDocuments(hub.items);
    } catch {
      setHubDocuments([]);
    } finally {
      setHubLoading(false);
    }
  }

  async function refreshDocumentHubHistory() {
    setHubHistoryLoading(true);
    try {
      const hub = await listDocumentHub();
      setHubHistory(hub.items);
    } catch {
      setHubHistory([]);
    } finally {
      setHubHistoryLoading(false);
    }
  }

  function handleBatchUpload() {
    const documents = collectBatchDocuments();

    if (documents.length === 0) {
      setError("Add at least one document to the shipment batch.");
      return;
    }
    setError(null);
    setPendingBatchDocuments(documents);
    setShowHubPrompt(true);
  }

  async function submitBatchUpload(saveToDocumentHub: boolean) {
    const documents = pendingBatchDocuments ?? collectBatchDocuments();
    if (documents.length === 0) {
      setError("Add at least one document to the shipment batch.");
      setShowHubPrompt(false);
      return;
    }
    setShowHubPrompt(false);
    setBatchLoading(true);
    setError(null);
    setBatchResult(null);
    try {
      const resp = await uploadDocumentBatch({
        tenant_id: tenantId,
        save_to_document_hub: saveToDocumentHub,
        shipment_profile: shipmentProfile,
        documents,
      });
      setBatchResult(resp);
      setBatchAnalysisId(resp.batch_id);
      setPendingBatchDocuments(null);
      if (saveToDocumentHub) {
        await refreshDocumentHub(resp.batch_id);
        await refreshDocumentHubHistory();
      } else {
        setHubDocuments([]);
      }
    } catch (err) {
      setError(formatApiError(err, "Batch upload failed."));
    } finally {
      setBatchLoading(false);
    }
  }

  async function handleQuickAnalyze() {
    if (!batchAnalysisId.trim()) {
      setError(
        "Enter a Shipment Batch Analysis ID to quick analyze saved hub documents.",
      );
      return;
    }
    setBatchLoading(true);
    setError(null);
    setBatchResult(null);
    try {
      const resp = await analyzeDocumentHubBatch({
        tenant_id: tenantId,
        batch_id: batchAnalysisId.trim(),
        batch_name: batchResult?.batch_name,
        shipment_reference: batchResult?.shipment_reference || undefined,
        shipment_profile: shipmentProfile,
      });
      setBatchResult(resp);
      await refreshDocumentHub(resp.batch_id);
      await refreshDocumentHubHistory();
    } catch (err) {
      setError(formatApiError(err, "Quick analyze failed."));
    } finally {
      setBatchLoading(false);
    }
  }

  async function quickAnalyzeHubBatch(item: DocumentHubItem) {
    setBatchAnalysisId(item.batch_id);
    setBatchLoading(true);
    setError(null);
    setBatchResult(null);
    try {
      const resp = await analyzeDocumentHubBatch({
        tenant_id: tenantId,
        batch_id: item.batch_id,
        batch_name: item.batch_name,
        shipment_reference: item.shipment_reference || undefined,
      });
      setBatchResult(resp);
      await refreshDocumentHub(resp.batch_id);
      await refreshDocumentHubHistory();
    } catch (err) {
      setError(formatApiError(err, "Quick analyze failed."));
    } finally {
      setBatchLoading(false);
    }
  }

  async function handleTrack() {
    if (!trackDocId.trim()) return;
    setTrackLoading(true);
    setTrackResult(null);
    try {
      const resp = await getDocumentStatus(trackDocId.trim());
      setTrackResult(resp);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Document not found");
    } finally {
      setTrackLoading(false);
    }
  }

  const hubHistoryByBatch = Object.values(
    hubHistory.reduce<
      Record<string, { batch: DocumentHubItem; documents: DocumentHubItem[] }>
    >((groups, item) => {
      if (!groups[item.batch_id]) {
        groups[item.batch_id] = { batch: item, documents: [] };
      }
      groups[item.batch_id].documents.push(item);
      return groups;
    }, {}),
  );

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="flex items-center gap-2 mb-6">
        <FileText size={24} className="text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-900">Document Workspace</h1>
      </div>

      {showHubPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/50 px-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Save batch documents to Document Hub?
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  Saved documents stay linked to the generated Shipment Batch
                  Analysis ID for future Quick Analyze runs.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowHubPrompt(false);
                  setPendingBatchDocuments(null);
                }}
                className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="Close document hub prompt"
              >
                <X size={16} />
              </button>
            </div>

            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
              <p id="document-hub-terms">
                Saving confirms the uploaded shipment documents may be retained
                inside LightLink AI for tenant-scoped compliance review, audit
                traceability, and re-analysis.
              </p>
              <p id="document-hub-guidelines">
                Upload only documents relevant to the same shipment case. Avoid
                unrelated personal data, duplicate scans, and files outside your
                client authorization.
              </p>
              <div className="flex flex-wrap gap-3 font-semibold text-emerald-700">
                <a
                  href="#document-hub-terms"
                  className="hover:text-emerald-900"
                >
                  Terms and conditions
                </a>
                <a
                  href="#document-hub-guidelines"
                  className="hover:text-emerald-900"
                >
                  Upload guidelines
                </a>
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => submitBatchUpload(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Skip Hub
              </button>
              <button
                type="button"
                onClick={() => submitBatchUpload(true)}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                Save & Analyze
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Upload size={16} className="text-blue-600" />
            Document Upload
          </h2>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="dw-tenant-id"
                className="block text-xs font-medium text-gray-700 mb-1"
              >
                Tenant ID
              </label>
              <input
                id="dw-tenant-id"
                type="text"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label
                htmlFor="dw-doc-type"
                className="block text-xs font-medium text-gray-700 mb-1"
              >
                Document Type
              </label>
              <select
                id="dw-doc-type"
                value={documentType}
                onChange={(e) =>
                  setDocumentType(e.target.value as ComplianceDocumentType)
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              >
                {DOCUMENT_TYPES.map((dt) => (
                  <option key={dt.value} value={dt.value}>
                    {dt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                File Upload
              </label>
              <div
                className={`rounded-lg border border-dashed px-3 py-3 transition ${
                  singleUploadDragActive
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 bg-white"
                }`}
                onDragEnterCapture={(e) => {
                  if (e.dataTransfer.types.includes("Files")) {
                    e.preventDefault();
                    setSingleUploadDragActive(true);
                  }
                }}
                onDragOverCapture={(e) => {
                  if (e.dataTransfer.types.includes("Files")) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "copy";
                    setSingleUploadDragActive(true);
                  }
                }}
                onDropCapture={(e) => {
                  if (e.dataTransfer.types.includes("Files")) {
                    e.preventDefault();
                    handleSingleUploadDrop(e);
                  }
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  setSingleUploadDragActive(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "copy";
                  setSingleUploadDragActive(true);
                }}
                onDragLeave={handleSingleUploadDragLeave}
                onDrop={handleSingleUploadDrop}
              >
                {selectedFile ? (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2 text-sm text-blue-800">
                      <Paperclip size={14} className="shrink-0" />
                      <span className="truncate">{selectedFile.name}</span>
                      <span className="shrink-0 text-xs text-blue-500">
                        ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="dw-single-file-input"
                        className="cursor-pointer rounded-md border border-blue-200 px-2 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                      >
                        Replace
                      </label>
                      <button
                        type="button"
                        onClick={clearFile}
                        className="text-blue-400 hover:text-blue-700"
                        aria-label="Remove file"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <label
                    htmlFor="dw-single-file-input"
                    className="flex cursor-pointer items-center gap-2 text-sm text-gray-500 hover:text-blue-600"
                  >
                    <Paperclip size={14} />
                    <span>Drag and drop a file here, or click to choose</span>
                  </label>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  You can drop a file here at any time to replace the current
                  selection.
                </p>
                <input
                  id="dw-single-file-input"
                  type="file"
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg,.tiff,.txt,.xml,.csv"
                  onChange={handleFileChange}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Source Text
                {selectedFile ? " (ignored when file is selected)" : ""}
              </label>
              <textarea
                value={sourceText}
                onChange={(e) => setSourceText(e.target.value)}
                disabled={!!selectedFile}
                rows={5}
                placeholder="Paste invoice text, eCMR content, or document body here..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Filename (optional)
              </label>
              <input
                type="text"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                placeholder="invoice-001.pdf"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-900">
                Amendment / Corrected Document
              </p>
              <p className="mt-1 text-xs text-amber-700">
                Use this when uploading a corrected invoice, packing list, or
                declaration. The previous version stays in audit history.
              </p>
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-amber-900 mb-1">
                    Previous Document ID
                  </label>
                  <input
                    type="text"
                    value={amendmentOfDocumentId}
                    onChange={(e) => setAmendmentOfDocumentId(e.target.value)}
                    placeholder="doc_abc123..."
                    className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-amber-900 mb-1">
                    Amendment Reason
                  </label>
                  <input
                    type="text"
                    value={amendmentReason}
                    onChange={(e) => setAmendmentReason(e.target.value)}
                    placeholder="Corrected invoice value, updated HS code..."
                    className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleUpload}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2 rounded-lg flex items-center justify-center gap-2 transition"
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Upload size={16} />
              )}
              {loading ? "Processing…" : "Upload & Ingest"}
            </button>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg flex items-start gap-2">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                {error}
              </div>
            )}
          </div>

          {/* Upload Result */}
          {result && (
            <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle size={16} className="text-green-600" />
                <span className="text-sm font-semibold text-green-800">
                  Document Ingested
                </span>
              </div>
              <div className="text-xs text-green-700 space-y-1">
                <p>
                  <strong>Document ID:</strong> {result.document_id}
                </p>
                <p>
                  <strong>Workflow ID:</strong> {result.workflow_id}
                </p>
                <p>
                  <strong>Classification:</strong> {result.classification}
                </p>
                <p>
                  <strong>Version:</strong> v{result.version}{" "}
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                      result.is_latest_version
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {result.is_latest_version ? "Latest" : "Superseded"}
                  </span>
                </p>
                {result.amendment_of_document_id && (
                  <p>
                    <strong>Amends:</strong>{" "}
                    <span className="font-mono">
                      {result.amendment_of_document_id}
                    </span>
                  </p>
                )}
                <p>
                  <strong>OCR Status:</strong> {result.ocr_status}
                </p>
                <p>
                  <strong>Validation:</strong> {result.validation_status}
                </p>
                <p>
                  <strong>Risks Created:</strong> {result.created_risks}
                </p>
                {Object.keys(result.extracted_fields).length > 0 && (
                  <div className="mt-2">
                    <strong>Extracted Fields:</strong>
                    <ul className="list-disc list-inside mt-1">
                      {Object.entries(result.extracted_fields).map(([k, v]) => (
                        <li key={k}>
                          {k}: <span className="font-mono">{v}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Track Document */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Search size={16} className="text-purple-600" />
            Track Document
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Document ID
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={trackDocId}
                  onChange={(e) => setTrackDocId(e.target.value)}
                  placeholder="doc_abc123..."
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleTrack}
                  disabled={trackLoading}
                  className="bg-gray-700 hover:bg-gray-800 disabled:bg-gray-400 text-white px-4 rounded-lg text-sm font-medium transition"
                >
                  {trackLoading ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    "Search"
                  )}
                </button>
              </div>
            </div>

            {trackResult && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-800">
                    {trackResult.document_id}
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      trackResult.status === "processed"
                        ? "bg-green-100 text-green-800"
                        : "bg-amber-100 text-amber-800"
                    }`}
                  >
                    {trackResult.status}
                  </span>
                </div>
                <div className="text-xs text-gray-600 space-y-1">
                  <p>
                    <strong>Type:</strong> {trackResult.document_type}
                  </p>
                  <p>
                    <strong>Classification:</strong>{" "}
                    {trackResult.classification}
                  </p>
                  <p>
                    <strong>OCR:</strong> {trackResult.ocr_status}
                  </p>
                  <p>
                    <strong>Parsing:</strong> {trackResult.parsing_status}
                  </p>
                  <p>
                    <strong>Validation:</strong> {trackResult.validation_status}
                  </p>
                  <p>
                    <strong>Version:</strong> v{trackResult.version}{" "}
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                        trackResult.is_latest_version
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {trackResult.is_latest_version ? "Latest" : "Superseded"}
                    </span>
                  </p>
                  {trackResult.amendment_of_document_id && (
                    <p>
                      <strong>Amends:</strong>{" "}
                      <span className="font-mono">
                        {trackResult.amendment_of_document_id}
                      </span>
                    </p>
                  )}
                  {trackResult.superseded_by_document_id && (
                    <p>
                      <strong>Superseded By:</strong>{" "}
                      <span className="font-mono">
                        {trackResult.superseded_by_document_id}
                      </span>
                    </p>
                  )}
                  {trackResult.amendment_reason && (
                    <p>
                      <strong>Reason:</strong> {trackResult.amendment_reason}
                    </p>
                  )}
                  {trackResult.missing_required_fields.length > 0 && (
                    <div className="mt-2">
                      <strong className="text-red-600">
                        Missing Required Fields:
                      </strong>
                      <ul className="list-disc list-inside mt-1">
                        {trackResult.missing_required_fields.map((f) => (
                          <li key={f} className="text-red-500">
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {Object.keys(trackResult.extracted_fields).length > 0 && (
                    <div className="mt-2">
                      <strong>Fields:</strong>
                      <ul className="list-disc list-inside mt-1">
                        {Object.entries(trackResult.extracted_fields).map(
                          ([k, v]) => (
                            <li key={k}>
                              {k}: {v}
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Batch Upload */}
      <div className="mt-6 bg-white border border-gray-200 rounded-lg shadow-sm p-6">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Upload size={16} className="text-emerald-600" />
              Shipment Batch Analysis
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Upload one shipment packet with core logistics, customs, origin,
              dangerous goods, cold chain, delivery, license, and inspection
              evidence.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={handleBatchUpload}
              disabled={batchLoading}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
            >
              {batchLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <CheckCircle size={16} />
              )}
              {batchLoading ? "Analyzing..." : "Analyze Batch"}
            </button>
            <button
              type="button"
              onClick={handleQuickAnalyze}
              disabled={batchLoading}
              className="rounded-lg border border-emerald-600 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:border-gray-300 disabled:text-gray-400 flex items-center justify-center gap-2"
            >
              {batchLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Search size={16} />
              )}
              Quick Analyze
            </button>
          </div>
        </div>

        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Shipment Batch Analysis ID
            </label>
            <input
              value={batchAnalysisId}
              onChange={(e) => setBatchAnalysisId(e.target.value)}
              placeholder="batch_abc123..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>

        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">
                Logistics Audit Profile
              </p>
              <p className="text-xs text-gray-500">
                LightLink uses this context to decide which audit documents are
                required or recommended for this shipment case.
              </p>
            </div>
            <select
              value={shipmentProfile.movement_type ?? "eu_export"}
              onChange={(event) =>
                updateShipmentProfile({
                  movement_type: event.target
                    .value as ShipmentProfile["movement_type"],
                })
              }
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              aria-label="Movement type"
            >
              <option value="eu_export">EU export</option>
              <option value="eu_import">EU import</option>
              <option value="intra_eu">Intra-EU movement</option>
              <option value="customs_transit">Customs transit</option>
            </select>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {SHIPMENT_PROFILE_OPTIONS.map((option) => (
              <label
                key={option.key}
                className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700"
              >
                <input
                  type="checkbox"
                  checked={Boolean(shipmentProfile[option.key])}
                  onChange={(event) =>
                    updateShipmentProfile({
                      [option.key]: event.target.checked,
                    })
                  }
                  className="h-4 w-4 rounded border-gray-300 text-emerald-600"
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>

        {(hubLoading || hubDocuments.length > 0) && (
          <div className="mb-4 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            {hubLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 size={13} className="animate-spin" />
                Loading Document Hub records...
              </span>
            ) : (
              <span>
                Document Hub: {hubDocuments.length} saved document
                {hubDocuments.length === 1 ? "" : "s"} linked to{" "}
                {hubDocuments[0]?.batch_id}.
              </span>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-5">
          {batchSlots.map((slot) => {
            const hasContent = !!slot.fileBase64 || !!slot.sourceText.trim();
            return (
              <div
                key={slot.documentType}
                className={`rounded-lg border p-3 ${
                  hasContent
                    ? "border-emerald-200 bg-emerald-50"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-gray-900">
                    {slot.label}
                  </p>
                  {hasContent && (
                    <button
                      type="button"
                      onClick={() => clearBatchSlot(slot.documentType)}
                      className="text-gray-400 hover:text-red-600"
                      aria-label={`Clear ${slot.label}`}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                <div
                  className={`mb-2 rounded-md border border-dashed bg-white px-2 py-2 text-xs transition ${
                    batchSlotDragActive[slot.documentType]
                      ? "border-emerald-500 text-emerald-700"
                      : "border-gray-300 text-gray-500 hover:border-emerald-400 hover:text-emerald-700"
                  }`}
                  onDragEnterCapture={(e) => {
                    if (e.dataTransfer.types.includes("Files")) {
                      e.preventDefault();
                      setBatchSlotDrag(slot.documentType, true);
                    }
                  }}
                  onDragOverCapture={(e) => {
                    if (e.dataTransfer.types.includes("Files")) {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "copy";
                      setBatchSlotDrag(slot.documentType, true);
                    }
                  }}
                  onDropCapture={(e) => {
                    if (e.dataTransfer.types.includes("Files")) {
                      e.preventDefault();
                      handleBatchFileDrop(slot.documentType, e);
                    }
                  }}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    setBatchSlotDrag(slot.documentType, true);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "copy";
                    setBatchSlotDrag(slot.documentType, true);
                  }}
                  onDragLeave={(e) =>
                    handleBatchSlotDragLeave(slot.documentType, e)
                  }
                  onDrop={(e) => handleBatchFileDrop(slot.documentType, e)}
                >
                  {slot.fileBase64 ? (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-1 truncate text-emerald-800">
                        <Paperclip size={12} className="shrink-0" />
                        <span className="truncate">{slot.filename}</span>
                      </div>
                      <label
                        htmlFor={`batch-file-${slot.documentType}`}
                        className="cursor-pointer rounded border border-emerald-200 px-2 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50"
                      >
                        Replace
                      </label>
                    </div>
                  ) : (
                    <label
                      htmlFor={`batch-file-${slot.documentType}`}
                      className="flex cursor-pointer items-center justify-center gap-1"
                    >
                      <Paperclip size={12} />
                      Drag file here or click
                    </label>
                  )}
                  <input
                    id={`batch-file-${slot.documentType}`}
                    type="file"
                    className="hidden"
                    accept=".pdf,.png,.jpg,.jpeg,.tiff,.txt,.xml,.csv"
                    onChange={(e) =>
                      handleBatchFileChange(
                        slot.documentType,
                        e.target.files?.[0],
                      )
                    }
                  />
                </div>
                <textarea
                  value={slot.sourceText}
                  onChange={(e) =>
                    updateBatchSlot(slot.documentType, {
                      sourceText: e.target.value,
                      fileBase64: null,
                      filename: "",
                      mimeType: "text/plain",
                    })
                  }
                  disabled={!!slot.fileBase64}
                  rows={4}
                  placeholder={`${slot.label} text`}
                  className="w-full resize-none rounded-md border border-gray-300 px-2 py-2 text-xs focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100 disabled:text-gray-400"
                />
              </div>
            );
          })}
        </div>

        {batchResult && (
          <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <div className="mb-2 flex items-start justify-between gap-2">
                <div>
                  <span className="text-sm font-semibold text-emerald-900">
                    {batchResult.batch_name}
                  </span>
                  <span className="ml-2 rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-emerald-700">
                    {batchResult.status}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={downloadBatchReportPdf}
                    className="flex items-center gap-1 rounded-md bg-emerald-700 px-2 py-1 text-xs font-semibold text-white hover:bg-emerald-800"
                  >
                    <Download size={12} />
                    PDF
                  </button>
                  <button
                    type="button"
                    onClick={downloadBatchReport}
                    className="flex items-center gap-1 rounded-md border border-emerald-700 px-2 py-1 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
                  >
                    <Download size={12} />
                    JSON
                  </button>
                </div>
              </div>
              <div className="space-y-1 text-xs text-emerald-800">
                <p>
                  <strong>Batch ID:</strong> {batchResult.batch_id}
                </p>
                <p>
                  <strong>Shipment:</strong>{" "}
                  {batchResult.shipment_reference || "Not detected"}
                </p>
                <p>
                  <strong>Documents:</strong> {batchResult.documents.length}
                </p>
              </div>
              <ul className="mt-3 list-disc space-y-1 pl-4 text-xs text-emerald-800">
                {batchResult.report_summary.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="mb-3 text-sm font-semibold text-gray-900">
                Relational Document Tree
              </p>
              <div className="space-y-2">
                {batchResult.relationship_tree.map((node) => (
                  <div
                    key={node.document_id}
                    className="rounded-md border border-gray-100 bg-gray-50 p-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-gray-800">
                        {node.document_type.replace(/_/g, " ")}
                      </span>
                      <span className="font-mono text-[10px] text-gray-400">
                        {node.document_id}
                      </span>
                    </div>
                    <p className="mt-1 text-[11px] text-gray-500">
                      Linked docs: {node.links.length || 0}
                    </p>
                    {node.missing_fields.length > 0 && (
                      <p className="mt-1 text-[11px] text-red-600">
                        Missing: {node.missing_fields.join(", ")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="mb-3 text-sm font-semibold text-gray-900">
                Missing & Mismatch Review
              </p>
              {batchResult.missing_document_types.length > 0 ? (
                <div className="mb-3 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
                  Missing documents:{" "}
                  {batchResult.missing_document_types.join(", ")}
                </div>
              ) : (
                <div className="mb-3 rounded-md bg-emerald-50 p-2 text-xs text-emerald-800">
                  All required document types are present.
                </div>
              )}
              <div className="space-y-2">
                {batchResult.mismatches.map((item) => (
                  <div
                    key={`${item.field}-${item.message}`}
                    className="rounded-md border border-red-100 bg-red-50 p-2"
                  >
                    <p className="text-xs font-semibold text-red-800">
                      {item.message}
                    </p>
                    <ul className="mt-1 space-y-0.5 text-[11px] text-red-700">
                      {Object.entries(item.values).map(([docType, value]) => (
                        <li key={docType}>
                          {docType.replace(/_/g, " ")}: {value}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {batchResult.mismatches.length === 0 && (
                  <p className="text-xs text-gray-500">
                    No cross-document mismatches detected.
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4 lg:col-span-3">
              <p className="mb-3 text-sm font-semibold text-gray-900">
                Logistics Audit Document Checklist
              </p>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {batchResult.document_requirements.map((item) => (
                  <div
                    key={item.document_type}
                    className={`rounded-md border p-3 ${
                      item.status === "present"
                        ? "border-emerald-100 bg-emerald-50"
                        : item.level === "required"
                          ? "border-red-100 bg-red-50"
                          : "border-amber-100 bg-amber-50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs font-semibold text-gray-900">
                        {item.label}
                      </p>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          item.status === "present"
                            ? "bg-emerald-100 text-emerald-700"
                            : item.level === "required"
                              ? "bg-red-100 text-red-700"
                              : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {item.status}
                      </span>
                    </div>
                    <p className="mt-1 text-[11px] font-medium uppercase text-gray-500">
                      {item.level}
                    </p>
                    <p className="mt-2 text-[11px] leading-relaxed text-gray-600">
                      {item.reason}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4 lg:col-span-3">
              <p className="mb-3 text-sm font-semibold text-gray-900">
                Compliance Field Matrix
              </p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-left text-xs">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50 text-gray-500">
                      <th className="px-3 py-2 font-semibold">Field</th>
                      {DOCUMENT_TYPES.map((doc) => (
                        <th key={doc.value} className="px-3 py-2 font-semibold">
                          {doc.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {Object.entries(batchResult.field_matrix).map(
                      ([field, values]) => (
                        <tr key={field}>
                          <td className="px-3 py-2 font-semibold text-gray-800">
                            {field.replace(/_/g, " ")}
                          </td>
                          {DOCUMENT_TYPES.map((doc) => (
                            <td
                              key={doc.value}
                              className="px-3 py-2 text-gray-600"
                            >
                              {values[doc.value] || (
                                <span className="text-gray-300">Missing</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ),
                    )}
                    {Object.keys(batchResult.field_matrix).length === 0 && (
                      <tr>
                        <td
                          colSpan={DOCUMENT_TYPES.length + 1}
                          className="px-3 py-6 text-center text-gray-500"
                        >
                          No structured fields were extracted from this batch.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4 lg:col-span-3">
              <p className="mb-3 text-sm font-semibold text-gray-900">
                Document-Level Missing Information
              </p>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                {batchResult.documents.map((doc) => (
                  <div
                    key={doc.document_id}
                    className="rounded-md border border-gray-100 bg-gray-50 p-3"
                  >
                    <p className="text-xs font-semibold text-gray-900">
                      {doc.document_type.replace(/_/g, " ")}
                    </p>
                    <p className="mt-1 font-mono text-[10px] text-gray-400">
                      {doc.document_id}
                    </p>
                    {doc.missing_required_fields.length > 0 ? (
                      <ul className="mt-2 list-disc space-y-1 pl-4 text-[11px] text-red-600">
                        {doc.missing_required_fields.map((field) => (
                          <li key={field}>{field.replace(/_/g, " ")}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-2 text-[11px] text-emerald-700">
                        Required fields complete.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Document Hub History */}
      <div className="mt-6 bg-white border border-gray-200 rounded-lg shadow-sm p-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2 font-semibold text-gray-900">
            <Database size={16} className="text-emerald-600" />
            Document Hub History
            <span className="text-xs font-normal text-gray-500 ml-1">
              Saved batches grouped by Shipment Batch Analysis ID
            </span>
          </span>
          <button
            type="button"
            onClick={() => void refreshDocumentHubHistory()}
            disabled={hubHistoryLoading}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:text-gray-400 flex items-center justify-center gap-2"
          >
            {hubHistoryLoading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Search size={14} />
            )}
            Refresh
          </button>
        </div>

        {hubHistoryLoading ? (
          <div className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-4 text-sm text-gray-500">
            <Loader2 size={14} className="animate-spin" />
            Loading saved Document Hub history...
          </div>
        ) : hubHistoryByBatch.length === 0 ? (
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-4 text-sm text-gray-500">
            No saved Document Hub batches yet. Use Save & Analyze on a shipment
            batch to store it here.
          </div>
        ) : (
          <div className="space-y-3">
            {hubHistoryByBatch.map(({ batch, documents }) => (
              <div
                key={batch.batch_id}
                className="rounded-lg border border-gray-200 bg-gray-50 p-4"
              >
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">
                        {batch.batch_name}
                      </span>
                      <span className="rounded-full bg-white px-2 py-0.5 font-mono text-[10px] text-gray-500">
                        {batch.batch_id}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span>
                        Shipment: {batch.shipment_reference || "Not captured"}
                      </span>
                      <span>Documents: {documents.length}</span>
                      <span>
                        Saved: {new Date(batch.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => void quickAnalyzeHubBatch(batch)}
                    disabled={batchLoading}
                    className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
                  >
                    {batchLoading ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : (
                      <Search size={13} />
                    )}
                    Quick Analyze
                  </button>
                </div>
                <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-5">
                  {documents.map((doc) => (
                    <div
                      key={doc.document_id}
                      className="rounded-md border border-gray-200 bg-white p-3"
                    >
                      <p className="text-xs font-semibold capitalize text-gray-800">
                        {doc.document_type.replace(/_/g, " ")}
                      </p>
                      <p className="mt-1 truncate text-[11px] text-gray-500">
                        {doc.filename || "Stored document"}
                      </p>
                      <p className="mt-1 font-mono text-[10px] text-gray-400">
                        {doc.document_id}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                          {doc.validation_status}
                        </span>
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            doc.is_latest_version
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          v{doc.version}{" "}
                          {doc.is_latest_version ? "Latest" : "Superseded"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
